import {
  createBrowserLogger,
  installGlobalErrorCapture,
  type BrowserLogger,
} from '@speles7172/log-client/browser';
import { refreshAccessToken } from '../api/client';
import { readTokenClaims } from './accessToken';

/** The source the API files this browser's records under (backend `BROWSER_LOG_SOURCE`). */
export const BROWSER_LOG_SOURCE = 'peles-web';

/** Where the records go: `POST /api/v1/logs`, signed in. */
export const BROWSER_LOG_ENDPOINT = '/api/v1/logs';

let active: { logger: BrowserLogger; uninstall: () => void } | null = null;

/** A token this close to expiry is refreshed before a batch rides on it. */
export const TOKEN_EXPIRY_SLACK_MS = 30_000;

/** Whether an administrator is impersonating somebody (`AuthContext` keeps their own tokens aside). */
function impersonating(): boolean {
  try {
    return localStorage.getItem('real_access_token') !== null;
  } catch {
    return false;
  }
}

function readToken(): string | null {
  try {
    return localStorage.getItem('access_token');
  } catch {
    return null;
  }
}

/** Whether a JWT has expired (or will within the slack); a token it cannot read counts as live. */
export function tokenExpired(token: string, now = Date.now()): boolean {
  const exp = readTokenClaims(token)?.exp;
  return typeof exp === 'number' && exp * 1000 - TOKEN_EXPIRY_SLACK_MS <= now;
}

/**
 * The signed-in person's token, as `apiClient` sends it. The logger posts with
 * `fetch`, outside `apiClient`'s refresh-on-401, so a batch sent after the
 * access token expired — a tab left open, and then it crashes — was refused
 * and the crash lost. An expired token is refreshed first, through
 * `apiClient`'s own single refresh (`refreshAccessToken`) — never through a
 * request whose failure would sign the person out. Never while impersonating:
 * the stored refresh token is then the administrator's own, and refreshing
 * with it would swap the impersonated session for theirs.
 */
async function authHeaders(): Promise<Record<string, string>> {
  let token = readToken();
  if (token && tokenExpired(token) && !impersonating()) {
    try {
      await refreshAccessToken();
    } catch {
      // Refresh failed: send as we are. The API refuses it, but the session is
      // left alone — signing somebody out is for their own requests to decide.
    }
    token = readToken();
  }
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Starts sending this page's crashes to the Logs screen
 * (`@speles7172/log-client/browser`): an uncaught error or an unhandled
 * rejection becomes a record, batched and posted to the API, which files it
 * under whoever is signed in — so a blank page on somebody's phone shows up
 * beside the server's own records.
 *
 * Warnings and errors only: the browser is not where the application says
 * what happened, only where it went wrong. Started by the signed-in app
 * (`AppLayout`), never by the public site or a guest's page, which have
 * nobody to file a record under. The answer stops it again; a second start
 * while one runs is the same logger, and its answer stops nothing.
 */
export function startBrowserLogging(): () => void {
  // Already running: this caller neither started it nor stops it.
  if (active) return () => undefined;
  const logger = createBrowserLogger({
    endpoint: BROWSER_LOG_ENDPOINT,
    source: BROWSER_LOG_SOURCE,
    environment: import.meta.env.MODE,
    level: 'warn',
    headers: authHeaders,
  });
  const started = { logger, uninstall: installGlobalErrorCapture(logger) };
  active = started;
  return () => {
    if (active !== started) return;
    active = null;
    started.uninstall();
    void started.logger.close();
  };
}

/** The running browser logger, for a caught error worth reporting; null when stopped. */
export function browserLogger(): BrowserLogger | null {
  return active?.logger ?? null;
}
